import React from "react";
import { useTranslation } from "react-i18next";
import { Line, defaults } from "react-chartjs-2";
import "chartjs-adapter-luxon";
import {
  IBloodPressureWarningData,
  bpTimestampToMillis,
} from "../eventDataTypes";
import { IEvent } from "../../../services/dashboard.services";
import { LayoutChartContainer } from "./LayoutChartContainer";
import {
  DateFromIsoString,
  DateTimeFromMillis,
  timeConfigurations,
} from "../../../helpers/datetime";
import useCommunityUnitTimezone from "../hooks/useCommunityUnitTimezone";

defaults.font.family = "GothamRoundedBook";

export default function BloodPressureWarningChart(props: IProps) {
  const { event } = props;
  const { t } = useTranslation();
  const { locale } = timeConfigurations();
  const communityUnitTimezone = useCommunityUnitTimezone(
    event.community_id ?? undefined,
    event.unit_id
  );
  const eventDateTime = DateFromIsoString(
    event.time_created,
    communityUnitTimezone
  );

  let bpData: IBloodPressureWarningData | null = null;
  try {
    bpData = JSON.parse(event.data) as IBloodPressureWarningData;
  } catch {
    bpData = null;
  }

  const bpReadings = (bpData?.bp_readings || [])
    .slice()
    .sort(
      (a, b) =>
        bpTimestampToMillis(a.timestamp) - bpTimestampToMillis(b.timestamp)
    );

  const pulseReadings = (bpData?.pulse_readings || [])
    .slice()
    .sort(
      (a, b) =>
        bpTimestampToMillis(a.timestamp) - bpTimestampToMillis(b.timestamp)
    );

  const hasData = bpReadings.length > 0 || pulseReadings.length > 0;

  const allValues = [
    ...bpReadings.map((r) => r.systolic),
    ...bpReadings.map((r) => r.diastolic),
    ...pulseReadings.map((r) => r.pulse),
  ];

  let yMin = 60;
  let yMax = 180;
  if (allValues.length > 0) {
    const dataMin = Math.min(...allValues);
    const dataMax = Math.max(...allValues);
    yMin = Math.floor((dataMin - 10) / 20) * 20;
    yMax = Math.ceil((dataMax + 10) / 20) * 20;
    if (yMin < 0) yMin = 0;
  }

  const systolicData = bpReadings.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.systolic,
  }));

  const diastolicData = bpReadings.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.diastolic,
  }));

  const pulseData = pulseReadings.map((r) => ({
    x: bpTimestampToMillis(r.timestamp),
    y: r.pulse,
  }));

  const data = {
    datasets: [
      {
        label: t("systolic"),
        data: systolicData,
        borderColor: "#2196F3",
        backgroundColor: "#2196F3",
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
      {
        label: t("diastolic"),
        data: diastolicData,
        borderColor: "#000000",
        backgroundColor: "#000000",
        borderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
      {
        label: t("pulse"),
        data: pulseData,
        borderColor: "#E25C4E",
        backgroundColor: "#FFFFFF",
        pointBorderColor: "#E25C4E",
        pointBackgroundColor: "#FFFFFF",
        borderWidth: 2,
        pointRadius: 5,
        pointBorderWidth: 2,
        pointHoverRadius: 7,
        fill: false,
        tension: 0.1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false as const,
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          title: (items: any[]) => {
            if (items.length > 0) {
              const date = DateTimeFromMillis(
                items[0].parsed.x,
                communityUnitTimezone
              );
              return date.toFormat("EEE d, HH:mm");
            }
            return "";
          },
        },
      },
      legend: {
        display: true,
        position: "bottom" as const,
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          padding: 16,
        },
      },
    },
    scales: {
      x: {
        type: "time" as const,
        min: eventDateTime.minus({ days: 6 }).startOf("day").toMillis(),
        max: eventDateTime.endOf("day").toMillis(),
        time: {
          unit: "day" as const,
          displayFormats: {
            day: "EEE d",
          },
        },
        adapters: {
          date: {
            locale: locale,
            zone: communityUnitTimezone,
          },
        },
        ticks: {
          callback: (value: any) => {
            const parts = String(value).split(" ");
            return parts.length >= 2 ? [parts[0], parts[1]] : value;
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        min: yMin,
        max: yMax,
        ticks: {
          stepSize: 20,
        },
      },
    },
  };

  return (
    <LayoutChartContainer
      title={t("blood_pressure_chart_title").toUpperCase()}
      eventTimeCreated={event.time_created}
      eventType={event.event_type}
      unitID={event.unit_id}
      emptyData={!hasData}
      complementaryText=""
      endText=""
    >
      <Line type="line" data={data} options={options} />
    </LayoutChartContainer>
  );
}

interface IProps {
  event: IEvent;
}
